import type { CommandContext } from './analysis-commands';
import { canRedo, canUndo, createHistory, pushHistory, redo, undo, type History } from './history';
import type { Rejection } from './rejection';
import type { WorkspaceDocument } from './workspace-document';
import { applyWorkspaceCommand, type WorkspaceCommand } from './workspace-reducer';

/** Estado del documento con su historial. No incluye estado de UI ni resultados. */
export interface WorkspaceStoreState {
  readonly history: History<WorkspaceDocument>;
  /** Ultimo rechazo (con secuencia para que la UI lo anuncie aunque se repita el mensaje). */
  readonly rejection: { readonly sequence: number; readonly value: Rejection } | null;
  readonly lastCreatedCardId: string | null;
}

export type WorkspaceAction =
  | { readonly type: 'command'; readonly command: WorkspaceCommand }
  | { readonly type: 'undo' }
  | { readonly type: 'redo' }
  | { readonly type: 'reset'; readonly document: WorkspaceDocument }
  /** Rechazo detectado fuera del reducer (p. ej. un drop invalido); no toca el documento. */
  | { readonly type: 'report-rejection'; readonly rejection: Rejection }
  | { readonly type: 'dismiss-rejection' };

export function createWorkspaceStoreState(document: WorkspaceDocument): WorkspaceStoreState {
  return { history: createHistory(document), rejection: null, lastCreatedCardId: null };
}

/**
 * Reducer del documento: aplica comandos, registra el historial y conserva el ultimo
 * rechazo. Un comando rechazado no altera el historial ni el documento.
 */
export function workspaceStoreReducer(
  state: WorkspaceStoreState,
  action: WorkspaceAction,
  context: CommandContext,
): WorkspaceStoreState {
  switch (action.type) {
    case 'command': {
      const outcome = applyWorkspaceCommand(state.history.present, action.command, context);
      if (outcome.rejection) return withRejection(state, outcome.rejection);
      const history = pushHistory(state.history, outcome.document);
      if (history === state.history && state.rejection === null) return state;
      return { history, rejection: null, lastCreatedCardId: outcome.createdCardId };
    }
    case 'undo':
      return canUndo(state.history)
        ? { history: undo(state.history), rejection: null, lastCreatedCardId: null }
        : state;
    case 'redo':
      return canRedo(state.history)
        ? { history: redo(state.history), rejection: null, lastCreatedCardId: null }
        : state;
    case 'reset':
      return createWorkspaceStoreState(action.document);
    case 'report-rejection':
      return withRejection(state, action.rejection);
    case 'dismiss-rejection':
      return state.rejection === null ? state : { ...state, rejection: null };
  }
}

const withRejection = (state: WorkspaceStoreState, rejection: Rejection): WorkspaceStoreState => ({
  ...state,
  rejection: { sequence: (state.rejection?.sequence ?? 0) + 1, value: rejection },
});
